import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box, Typography, TextField, Button, Paper, Table, TableHead, TableBody,
  TableRow, TableCell, Chip, CircularProgress, Alert,
  TablePagination, InputAdornment,
} from '@mui/material'
import { Search, Save, ArrowLeft, Loader } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { searchDocuments, selectDocument, updateDocument } from '../../../redux/actions/dataUpdateAction'
import { DATA_UPDATE_SELECT_DOC_SUCCESS } from '../../../redux/actions/types'
import { backendClient } from '../../../api/backend'
import { backendUrl } from '../../../config'
import MetadataEditor from './MetadataEditor'
import RichHtmlEditor from './RichHtmlEditor'

export default function EditDocumentTab() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { countryCode, docType, editFlow, referenceData } = useSelector((s) => s.dataUpdate)
  const { search, selected, publishing, error } = editFlow

  const [query, setQuery] = useState('')
  const [editedMetadata, setEditedMetadata] = useState(null)
  const [editedHtml, setEditedHtml] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveProgress, setSaveProgress] = useState('')

  // Init edited state when document is selected
  useEffect(() => {
    if (selected.data && !editedMetadata) {
      setEditedMetadata(selected.data.metadata || {})
      setEditedHtml(selected.data.html_content || '')
    }
  }, [selected.data, editedMetadata])

  const handleSearch = (page = 1) => {
    dispatch(searchDocuments({
      query, country_code: countryCode, doc_type: docType, page, page_size: 15,
    }))
  }

  const handleSelect = (docId) => {
    setEditedMetadata(null)
    setEditedHtml(null)
    dispatch(selectDocument(docId, docType))
  }

  const waitForTask = (taskId) => {
    return new Promise((resolve, reject) => {
      const token = backendClient.getToken()
      const streamUrl = `${backendUrl}api/data-update/tasks/${taskId}/stream`
      fetch(streamUrl, {
        headers: token && token.access_token
          ? { Authorization: `Bearer ${token.access_token}` }
          : {},
      })
        .then(async (response) => {
          const reader = response.body.getReader()
          const decoder = new TextDecoder("utf-8")
          let buffer = ''
          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop()
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const event = JSON.parse(line.slice(6))
                  if (event.error) {
                    reject(new Error(event.message || 'Update failed'))
                    return
                  }
                  setSaveProgress(event.message || '')
                  if (event.step === 'complete') {
                    resolve(event.result)
                    return
                  }
                } catch (e) { /* ignore parse errors */ }
              }
            }
          }
          resolve(null)
        })
        .catch(reject)
    })
  }

  const handleSave = async () => {
    if (!editedMetadata || !selected.data) return
    setSaving(true)
    setSaveProgress('')
    try {
      const result = await dispatch(updateDocument(selected.data.id, {
        metadata: editedMetadata,
        html_content: editedHtml || null,
        doc_type: docType,
        country_code: countryCode,
        language: editedMetadata.language || 'fr',
      }))
      // Wait for background task to complete before refreshing
      if (result && result.task_id) {
        await waitForTask(result.task_id)
      }
      handleSearch(search.page)
    } catch (e) {
      // handled by redux
    }
    setSaving(false)
    setSaveProgress('')
  }

  const handleBack = () => {
    setEditedMetadata(null)
    setEditedHtml(null)
    dispatch({ type: DATA_UPDATE_SELECT_DOC_SUCCESS, payload: null })
  }

  // If a document is selected, show edit view
  if (selected.data && editedMetadata) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Button startIcon={<ArrowLeft size={16} />} onClick={handleBack} sx={{ textTransform: 'none' }}>
            {t('du_back_to_search')}
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              backgroundColor: '#c9a96e', '&:hover': { backgroundColor: '#b8955a' },
              textTransform: 'none', fontWeight: 600,
            }}
          >
            {saving ? (saveProgress || t('du_saving')) : t('du_save_changes')}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', gap: 3, minWidth: 0 }}>
          {/* Left: Metadata */}
          <Box sx={{ flex: '0 0 40%', minWidth: 0, maxHeight: 'calc(100vh - 180px)', overflow: 'auto' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>{t('du_metadata')}</Typography>
            <MetadataEditor
              metadata={editedMetadata}
              onChange={setEditedMetadata}
              docType={docType}
              referenceData={referenceData.data}
            />
          </Box>

          {/* Right: HTML */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{t('du_html_content')}</Typography>
            <RichHtmlEditor
              value={editedHtml}
              onChange={setEditedHtml}
              maxHeight="calc(100vh - 220px)"
            />
          </Box>
        </Box>

      </Box>
    )
  }

  // Search view
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          placeholder={t('du_search_documents')}
          size="small"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search size={18} /></InputAdornment>,
          }}
        />
        <Button
          variant="contained"
          onClick={() => handleSearch()}
          disabled={search.loading}
          sx={{
            backgroundColor: '#c9a96e', '&:hover': { backgroundColor: '#b8955a' },
            textTransform: 'none', minWidth: 100,
          }}
        >
          {t('du_search')}
        </Button>
      </Box>

      {selected.loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress sx={{ color: '#c9a96e' }} />
          <Typography sx={{ mt: 1 }}>{t('du_loading_document')}</Typography>
        </Box>
      )}

      {search.loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress sx={{ color: '#c9a96e' }} />
        </Box>
      ) : search.results.length > 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('du_title_header')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('du_number')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('du_language')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('du_status')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('du_date')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {search.results.map((doc) => (
                <TableRow
                  key={doc.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleSelect(doc.id)}
                >
                  <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.title}
                  </TableCell>
                  <TableCell>{doc.doc_number || '-'}</TableCell>
                  <TableCell>
                    <Chip label={doc.language?.toUpperCase()} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={doc.is_active ? t('du_active') : t('du_abrogated')}
                      size="small"
                      color={doc.is_active ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{doc.issue_date ? new Date(doc.issue_date).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    <Button size="small" sx={{ textTransform: 'none', color: '#c9a96e' }}>
                      {t('du_edit')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {search.total > 15 && (
            <TablePagination
              component="div"
              count={search.total}
              page={search.page - 1}
              rowsPerPage={15}
              rowsPerPageOptions={[15]}
              onPageChange={(_, p) => handleSearch(p + 1)}
            />
          )}
        </Paper>
      ) : search.query ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          {t('du_no_documents_found', { query: search.query })}
        </Typography>
      ) : (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          {t('du_search_to_edit')}
        </Typography>
      )}
    </Box>
  )
}
